import { useEffect, useMemo, useState } from 'react'
import { useApi, useApiMutation, useFormSaveStore } from '../../../utils'
import { launchToast, ToastTypes } from '../../../components'
import { convertOccupancyConfigToState } from '../../Lot/utils/convertOccupancyConfigToState'
import {
    GetOccupancyConfigQuerySchema,
    GetOccupancyConfigVariablesSchema,
    UpdateOccupancyConfigQuerySchema,
    UpdateOccupancyConfigVariablesSchema,
    type GetOccupancyConfigVariables,
    type UpdateOccupancyConfigVariables
} from '../occupancy_schemas'

export interface OccupancyConfigState {
    enabled: boolean
    lot_capacity_wl: number
    excluded_stations: string[]
    timeout: number
    timeout_wl: number
    lpness: number
    motorcycle: boolean
    truck: boolean
    undetected_vehicles: boolean
    version: string
    lpr: number
}
export const useOccupancyConfig = (selectedLotId: string | undefined) => {
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const [occupancyConfig, setOccupancyConfig] = useState<OccupancyConfigState>()

    const fetchVariables = useMemo<GetOccupancyConfigVariables | undefined>(
        () => (selectedLotId ? GetOccupancyConfigVariablesSchema.parse({ parking_lot: selectedLotId }) : undefined),
        [selectedLotId]
    )
    const fetchResult = useApi('/occupancy/get_occupancy_config', fetchVariables, GetOccupancyConfigQuerySchema, {
        queryKey: ['occupancy/get_occupancy_config', fetchVariables],
        enabled: !!fetchVariables
    })
    const rawOccupancyConfig = fetchResult.data
    const loadingFetchOccupancy = fetchResult.isFetching
    const refetch = fetchResult.refetch
    const errorFetchOccupancy = fetchResult.error

    const { mutate: updateOccupancyConfig, isPending: loadingUpdate } = useApiMutation<
        UpdateOccupancyConfigVariables,
        typeof UpdateOccupancyConfigQuerySchema
    >('/occupancy/update_occupancy_config', UpdateOccupancyConfigQuerySchema, {
        onError: (error) => {
            console.log(error)
            launchToast(ToastTypes.Error, 'Error: Could not update config!')
        },
        onSuccess: () => {
            launchToast(ToastTypes.Success, 'Successfully updated config!')
            refetch()
        }
    })

    const initialEnabled = rawOccupancyConfig?.data?.enabled ?? false

    const onHandleSubmit = () => {
        if (!occupancyConfig || !selectedLotId) return

        const currentEnabled = occupancyConfig.enabled
        if (currentEnabled !== initialEnabled) {
            const message = currentEnabled
                ? 'Are you sure you want to enable occupancy calculation?'
                : 'Are you sure you want to disable occupancy calculation?'

            const confirmed = window.confirm(message)
            if (!confirmed) return
        }

        const variables = UpdateOccupancyConfigVariablesSchema.parse({
            parking_lot: selectedLotId,
            live_occupancy_config: {
                excluded_stations: occupancyConfig.excluded_stations,
                lot_capacity_wl: occupancyConfig.lot_capacity_wl,
                timeout: occupancyConfig.timeout,
                timeout_wl: occupancyConfig.timeout_wl,
                filters: {
                    lpness: occupancyConfig.lpness,
                    motorcycle: occupancyConfig.motorcycle,
                    truck: occupancyConfig.truck,
                    undetected_vehicle: occupancyConfig.undetected_vehicles,
                    lpr: occupancyConfig.lpr
                },
                version: occupancyConfig.version,
                enabled: occupancyConfig.enabled
            }
        })
        updateOccupancyConfig(variables)
    }

    useEffect(() => {
        if (rawOccupancyConfig?.data) {
            const config = convertOccupancyConfigToState(rawOccupancyConfig.data)
            setOccupancyConfig(config)
        }
    }, [rawOccupancyConfig])

    useEffect(() => {
        if (rawOccupancyConfig?.data) {
            const initialConfig = convertOccupancyConfigToState(rawOccupancyConfig.data)
            const isDifferent = JSON.stringify(occupancyConfig) !== JSON.stringify(initialConfig)
            setFormChanges(isDifferent)
        }
    }, [occupancyConfig, rawOccupancyConfig])
    return {
        occupancyConfig,
        setOccupancyConfig,
        onHandleSubmit,
        loading: loadingFetchOccupancy || loadingUpdate,
        saving: loadingUpdate,
        errorsFetchingOccupancy: errorFetchOccupancy ? [errorFetchOccupancy] : undefined
    }
}
